import { Table, Select } from 'antd'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { PageHeader } from '../Common'
import { useAsync } from '../../hooks'
import useQueryParams from '../../hooks/useQueryParams'
import { useSelectedPractice } from '../../context/PracticeContext'
import { useIsAdmin } from '../../redux/slices/session'
import dayjs from '../../lib/datetime'
import api from '../../api'
import { getPaginationHeaders } from '../../redux/utils'
import { manufacturerLabel } from '../../lib/companies'
import { MANUFACTURERS } from '../Forms/ManufacturerSelect'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import ConnectivityAlerts from './ConnectivityAlerts'

const DEFAULT_MANUFACTURER = MANUFACTURERS[0].id
const DEFAULT_FILTERS = { manufacturer: DEFAULT_MANUFACTURER }
const QUERY_PARAMS_SCHEMA = { page: Number, pageSize: Number }

const MANUFACTURER_OPTIONS = MANUFACTURERS.map(({ id, name }) => ({
  value: id,
  label: name,
}))

const useStyles = createUseStyles({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 0',
  },
})

const columns = [
  {
    title: 'Practice',
    dataIndex: 'practiceName',
    key: 'practiceName',
  },
  {
    title: 'Manufacturer',
    dataIndex: 'manufacturer',
    key: 'manufacturer',
    render: manufacturerLabel,
  },
  {
    title: 'Last Received',
    dataIndex: 'lastReceivedAt',
    key: 'lastReceivedAt',
    render(value) {
      return value ? dayjs(value).format('MM/DD/YYYY HH:mm') : '—'
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    align: 'center',
    render(status) {
      return (
        <CircleCheckIcon
          checked={status === 'healthy'}
          falseStyle={{ color: 'red' }}
        />
      )
    },
  },
]

export default function Connectivity() {
  const theme = useTheme()
  const classes = useStyles({ theme })
  const isAdmin = useIsAdmin()
  const { selectedPracticeId } = useSelectedPractice()
  const practiceId = isAdmin ? selectedPracticeId : undefined

  const [conditions, setConditions] = useQueryParams({
    initialValues: DEFAULT_FILTERS,
    schema: QUERY_PARAMS_SCHEMA,
  })

  // Reset to the first page when the header practice selection changes.
  const prevPracticeRef = useRef(practiceId)
  useEffect(() => {
    if (prevPracticeRef.current === practiceId) return
    prevPracticeRef.current = practiceId
    setConditions({ ...conditions, page: 1 })
  }, [practiceId, conditions, setConditions])

  const fetchConnectivity = useCallback(
    () =>
      api.transmissions.connectivity({
        page: conditions.page,
        perPage: conditions.pageSize,
        practiceId,
        manufacturer: conditions.manufacturer,
      }),
    [conditions.page, conditions.pageSize, conditions.manufacturer, practiceId],
  )

  const { value, pending } = useAsync(fetchConnectivity)
  const rows = value?.body || []
  const { total, pageSize: defaultPageSize } = value
    ? getPaginationHeaders(value.headers)
    : {}

  const handlePageChange = useCallback(
    (page, pageSize) => setConditions({ ...conditions, page, pageSize }),
    [conditions, setConditions],
  )

  const handleManufacturerChange = useCallback(
    manufacturer => setConditions({ ...conditions, page: 1, manufacturer }),
    [conditions, setConditions],
  )

  const currentManufacturer = useMemo(
    () => conditions.manufacturer || DEFAULT_MANUFACTURER,
    [conditions.manufacturer],
  )

  return (
    <div>
      <PageHeader title="Transmissions Connectivity" />
      <div className={classes.toolbar}>
        <Select
          aria-label="Filter by manufacturer"
          style={{ width: 260 }}
          options={MANUFACTURER_OPTIONS}
          value={currentManufacturer}
          onChange={handleManufacturerChange}
        />
        <ConnectivityAlerts />
      </div>
      <Table
        columns={columns}
        dataSource={rows}
        loading={pending}
        rowKey={row => `${row.practiceId}-${row.manufacturer}`}
        pagination={{
          current: conditions.page || 1,
          pageSize: conditions.pageSize || defaultPageSize,
          onChange: handlePageChange,
          total,
          showSizeChanger: true,
        }}
      />
    </div>
  )
}
